import { assert, error } from '../../../app/logger.js';

/**
 * Event target base class.
 */
class EventedBase {
  constructor(options) {
    assert(options, `[map] no options`);
    this.#options = Object.assign(
      {},
      this.constructor.getDefaultOptions(),
      options
    );
  }

  get name() {
    return this.#options.name;
  }

  on(/* eventName, listener */) {
    error(`[map] on must be overriden`);
  }

  off(/* eventName, listener */) {
    error(`[map] off must be overriden`);
  }

  waitfor(eventName) {
    return new Promise(resolve => {
      let listenerHandle = this.on(eventName, () => {
        this.off(eventName, listenerHandle);
        resolve();
      });
    });
  }

  once(eventName, listener) {
    return new Promise(resolve => {
      let listenerHandle = this.on(eventName, async () => {
        this.off(eventName, listenerHandle);
        await listener();
        resolve();
      });
    });
  }

  // Protected

  get type() {
    return 'evented';
  }

  get nativeOptions() {
    if (!this.#nativeOptions) {
      this.#nativeOptions = this.getNativeOptions(this.options);
    }
    return this.#nativeOptions;
  }

  get options() {
    return this.#options;
  }

  getNativeOptions(options) {
    let nativeOptions = {};

    // Map options.
    const mapper = this.constructor.nativeOptionsMapper;
    for (let option in options) {
      let map = mapper[option];
      if (map) {
        let nativeOption = typeof map == 'string' ? map : map.name || option;
        let value = this.getNativeOption(option, options[option]);
        if (value !== undefined) {
          nativeOptions[nativeOption] = value;
        }
      }
    }

    // Apply default native options.
    let defaultOptions = this.constructor.getDefaultNativeOptions();
    for (let o in defaultOptions) {
      if (!(o in nativeOptions)) {
        nativeOptions[o] = defaultOptions[o];
      }
    }

    return nativeOptions;
  }

  getNativeOption(option, value) {
    const mapper = this.constructor.nativeOptionsMapper;
    let map = mapper[option];
    return map && typeof map == 'string'
      ? value
      : map instanceof Function
      ? map(value)
      : map.get(value);
  }

  static getDefaultOptions() {
    return {};
  }

  static getDefaultNativeOptions() {
    return {};
  }

  // Format is one of these:
  // {
  //   // 'name' option is mapped to 'nativeName' native option
  //   name: nativeName,
  //
  //   // 'name' option is mapped to 'name' native option with a value
  //   // returned from getValueFunc(option),
  //   name: getValueFunc,
  //
  //   // 'name' option is mapped to 'optionalNativeName' native name
  //   // (if given, otherwise into 'name' native option) with
  //   // a value returned from getValueFunc(option) function
  //   name: {
  //     name: optionalNativeName,
  //     getValue: getValueFunc,
  //   },
  // }
  static nativeOptionsMapper = {};

  #nativeOptions;
  #options;
}

export { EventedBase };
